import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, AlertTriangle, CheckCircle, Trash2, Activity, RefreshCw, Server, Globe, Code, Bell } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEVERITY_COLORS = {
  4: { label: 'CRITICAL', bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/40' },
  3: { label: 'ERROR',    bg: 'bg-orange-500/20', text: 'text-orange-400', border: 'border-orange-500/40' },
  2: { label: 'WARNING',  bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500/40' },
  1: { label: 'INFO',     bg: 'bg-zinc-500/20', text: 'text-zinc-400', border: 'border-zinc-500/40' },
};

const SOURCE_ICONS = {
  backend: Server,
  frontend: Globe,
  api_error: Code,
};

// Defined BEFORE the default export so the bundler can never produce a
// temporal-dead-zone reference when the parent component first renders.
function StatCard({ label, value, color, sub, testId }) {
  return (
    <div className="border border-zinc-800 p-4 bg-black" data-testid={testId}>
      <p className="font-mono text-[10px] text-zinc-500 tracking-widest mb-1">{label}</p>
      <p className={`font-mono text-2xl font-bold ${color}`}>{value}</p>
      {sub && <p className="font-mono text-[10px] text-zinc-600 mt-0.5">{sub}</p>}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-zinc-500 tracking-widest text-[10px]">{label}</p>
      <p className="text-white truncate">{String(value)}</p>
    </div>
  );
}

export default function AdminCrashes() {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState({ crashes: [], groups: [], stats: null });
  const [uptime, setUptime] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState('unresolved'); // unresolved | resolved | all
  const [tagFilter, setTagFilter] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [notifyConfig, setNotifyConfig] = useState(null);
  const [sendingTest, setSendingTest] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const params = new URLSearchParams();
      if (filter === 'unresolved') params.set('resolved', 'false');
      else if (filter === 'resolved') params.set('resolved', 'true');
      if (tagFilter) params.set('tag', tagFilter);
      params.set('limit', '500');

      const [c, u] = await Promise.all([
        axios.get(`${API}/admin/crashes?${params.toString()}`, { headers: getAuthHeader() }),
        axios.get(`${API}/admin/crashes/uptime?hours=24`, { headers: getAuthHeader() }),
      ]);
      setData(c.data || { crashes: [], groups: [], stats: null });
      setUptime(u.data || null);
    } catch (e) {
      toast.error('FAILED TO LOAD CRASH LOG');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, tagFilter]);

  useEffect(() => {
    axios.get(`${API}/admin/crashes/notify-config`, { headers: getAuthHeader() })
      .then(r => setNotifyConfig(r.data))
      .catch(() => setNotifyConfig({ recipient: null, configured: false }));
  }, [getAuthHeader]);

  const sendTestAlert = async () => {
    if (!notifyConfig?.recipient) return;
    setSendingTest(true);
    try {
      const res = await axios.post(`${API}/admin/crashes/send-test-alert`, {}, { headers: getAuthHeader() });
      toast.success(`SAMPLE SENT → ${res.data.sent_to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'FAILED TO SEND');
    } finally {
      setSendingTest(false);
    }
  };

  const bulkResolve = async (filter, label) => {
    if (!window.confirm(`Resolve ALL unresolved crashes matching: ${label}?\nThis can't be undone in bulk.`)) return;
    try {
      const res = await axios.post(`${API}/admin/crashes/bulk-resolve`, { ...filter, note: `Bulk resolved: ${label}` }, { headers: getAuthHeader() });
      toast.success(`RESOLVED ${res.data.resolved_count} CRASHES`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'BULK RESOLVE FAILED');
    }
  };

  const markResolved = async (id, resolved) => {
    try {
      await axios.patch(`${API}/admin/crashes/${id}`, { resolved }, { headers: getAuthHeader() });
      toast.success(resolved ? 'MARKED RESOLVED' : 'REOPENED');
      load();
    } catch {
      toast.error('FAILED');
    }
  };

  const saveNotes = async (id, notes) => {
    try {
      await axios.patch(`${API}/admin/crashes/${id}`, { admin_notes: notes }, { headers: getAuthHeader() });
      toast.success('NOTES SAVED');
    } catch {
      toast.error('FAILED');
    }
  };

  const deleteCrash = async (id) => {
    if (!window.confirm('Delete this crash record permanently?')) return;
    try {
      await axios.delete(`${API}/admin/crashes/${id}`, { headers: getAuthHeader() });
      toast.success('DELETED');
      load();
    } catch {
      toast.error('FAILED');
    }
  };

  const uptimePct = uptime?.uptime_pct ?? null;
  const incidents = uptime?.incidents || [];

  const stats = data.stats || { total: 0, unresolved: 0, last_24h: 0, last_7d: 0 };

  const filteredCrashes = useMemo(() => data.crashes || [], [data.crashes]);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-black border-b border-zinc-800">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="p-2 hover:bg-zinc-900"><ArrowLeft size={20} /></Link>
            <div>
              <h1 className="text-lg font-bold font-mono tracking-widest">STABILITY · CRASH LOG</h1>
              <p className="text-xs text-zinc-500 font-mono">RCA · UPTIME · INCIDENTS</p>
            </div>
          </div>
          <button onClick={load} disabled={refreshing} className="p-2 hover:bg-zinc-900" data-testid="refresh-crashes">
            <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Notification recipient banner */}
        <div className="border border-zinc-800 bg-zinc-950 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="notify-config-banner">
          <div className="flex items-center gap-3 min-w-0">
            <Bell className="w-4 h-4 text-tiffany-500 shrink-0" />
            <div className="min-w-0">
              <p className="font-mono text-[10px] text-zinc-500 tracking-widest">CRASH ALERTS WILL GO TO</p>
              <p className="font-mono text-sm text-tiffany-400 truncate" data-testid="notify-recipient">
                {notifyConfig === null ? 'LOADING_' : (notifyConfig.recipient || 'NOT CONFIGURED')}
              </p>
              {notifyConfig?.configured && (
                <p className="font-mono text-[10px] text-zinc-600 mt-0.5">EXCLUSIVE · NO OTHER ADMIN EMAILS WILL BE NOTIFIED</p>
              )}
            </div>
          </div>
          <button
            onClick={sendTestAlert}
            disabled={!notifyConfig?.configured || sendingTest}
            className="px-4 py-2 bg-tiffany-500 text-black font-mono text-xs tracking-widest hover:bg-tiffany-400 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
            data-testid="send-test-alert-btn"
          >
            {sendingTest ? 'SENDING_' : 'SEND SAMPLE'}
          </button>
        </div>

        {/* Top stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="UPTIME · 24H"
            value={uptimePct != null ? `${uptimePct.toFixed(2)}%` : '—'}
            color={uptimePct == null ? 'text-zinc-500' : uptimePct >= 99.9 ? 'text-tiffany-500' : uptimePct >= 99 ? 'text-yellow-400' : 'text-red-500'}
            sub={uptime?.avg_latency_ms != null ? `AVG ${uptime.avg_latency_ms}ms` : null}
            testId="stat-uptime"
          />
          <StatCard label="CRASHES · 24H"
            value={stats.last_24h}
            color={stats.last_24h === 0 ? 'text-tiffany-500' : stats.last_24h < 5 ? 'text-yellow-400' : 'text-red-500'}
            sub={`${stats.last_7d} IN 7D`}
            testId="stat-crashes-24h"
          />
          <StatCard label="UNRESOLVED"
            value={stats.unresolved}
            color={stats.unresolved === 0 ? 'text-tiffany-500' : 'text-orange-400'}
            sub={`${stats.total} TOTAL`}
            testId="stat-unresolved"
          />
          <StatCard label="INCIDENTS · 24H"
            value={incidents.length}
            color={incidents.length === 0 ? 'text-tiffany-500' : 'text-red-500'}
            sub={incidents.length > 0 ? 'CHECK DOWNTIME' : 'ALL GREEN'}
            testId="stat-incidents"
          />
        </div>

        {/* Sparkline */}
        {uptime?.sparkline?.length > 0 && (
          <div className="border border-zinc-800 p-5">
            <p className="font-mono text-xs text-zinc-500 tracking-widest mb-3 flex items-center gap-2">
              <Activity className="w-3.5 h-3.5" />
              UPTIME SPARKLINE · LAST {uptime.window_hours}H
            </p>
            <div className="flex items-end gap-px h-14">
              {uptime.sparkline.map((s, i) => {
                const maxLat = Math.max(1, ...uptime.sparkline.map(x => x.latency || 0));
                const h = s.status === 'down' ? '100%' : `${Math.max(8, ((s.latency || 0) / maxLat) * 100)}%`;
                const color = s.status === 'down' ? 'bg-red-500' : (s.latency || 0) > 1000 ? 'bg-yellow-500' : 'bg-tiffany-500/60';
                return (
                  <div key={i} className={`flex-1 ${color} relative group`} style={{ height: h }}>
                    <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 hidden group-hover:block bg-black border border-zinc-700 px-2 py-1 text-[10px] font-mono whitespace-nowrap z-10">
                      {new Date(s.t).toLocaleTimeString()} · {s.latency ?? 0}ms · {s.status.toUpperCase()}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Incidents list */}
        {incidents.length > 0 && (
          <div className="border border-red-500/30 p-5">
            <p className="font-mono text-xs text-red-400 tracking-widest mb-3">DOWNTIME INCIDENTS · {uptime.window_hours}H</p>
            <div className="space-y-2">
              {incidents.slice().reverse().map((inc, i) => (
                <div key={i} className="flex items-center justify-between gap-3 bg-zinc-950 border border-zinc-800 px-3 py-2">
                  <div className="font-mono text-xs">
                    <div className="text-red-400">{new Date(inc.start).toLocaleString()}</div>
                    <div className="text-zinc-500">→ {inc.end ? new Date(inc.end).toLocaleString() : 'ONGOING'}</div>
                  </div>
                  <div className="font-mono text-[11px] text-zinc-500 max-w-[60%] truncate text-right">
                    {inc.reason || 'NO REASON LOGGED'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tag groups */}
        {data.groups?.length > 0 && (
          <div className="border border-zinc-800 p-5">
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <p className="font-mono text-xs text-zinc-500 tracking-widest">UNRESOLVED · GROUPED BY ROOT CAUSE</p>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => bulkResolve({ severity_lte: 2 }, 'ALL WARNINGS')}
                  className="px-3 py-1 font-mono text-[10px] tracking-wider border border-yellow-500/40 text-yellow-400 hover:bg-yellow-500/10"
                  data-testid="bulk-resolve-warnings-btn"
                >
                  RESOLVE ALL WARNINGS
                </button>
                <button
                  onClick={() => bulkResolve({ all_unresolved: true }, 'EVERYTHING UNRESOLVED')}
                  className="px-3 py-1 font-mono text-[10px] tracking-wider border border-red-500/40 text-red-400 hover:bg-red-500/10"
                  data-testid="bulk-resolve-all-btn"
                >
                  RESOLVE ALL
                </button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setTagFilter('')}
                className={`px-3 py-1.5 font-mono text-[11px] tracking-wider border ${tagFilter === '' ? 'border-tiffany-500 text-tiffany-400 bg-tiffany-500/10' : 'border-zinc-700 text-zinc-400 hover:border-zinc-500'}`}
                data-testid="tag-filter-all"
              >
                ALL · {data.groups.reduce((a, g) => a + g.count, 0)}
              </button>
              {data.groups.map(g => (
                <div key={g.tag} className="flex items-stretch">
                  <button
                    onClick={() => setTagFilter(g.tag)}
                    className={`px-3 py-1.5 font-mono text-[11px] tracking-wider border-l border-y ${tagFilter === g.tag ? 'border-tiffany-500 text-tiffany-400 bg-tiffany-500/10' : 'border-zinc-700 text-zinc-400 hover:border-zinc-500'}`}
                    data-testid={`tag-filter-${g.tag}`}
                  >
                    {g.tag} · {g.count}
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); bulkResolve({ tag: g.tag }, `TAG: ${g.tag}`); }}
                    className={`px-2 font-mono text-[10px] tracking-wider border ${tagFilter === g.tag ? 'border-tiffany-500 text-tiffany-400 bg-tiffany-500/5 hover:bg-tiffany-500/15' : 'border-zinc-700 text-zinc-500 hover:text-tiffany-400 hover:border-tiffany-500/50'}`}
                    title={`Resolve all ${g.count} ${g.tag} crashes`}
                    data-testid={`bulk-resolve-${g.tag}-btn`}
                  >
                    ✓
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Crash list filters */}
        <div className="flex gap-2">
          {[['unresolved', 'UNRESOLVED'], ['resolved', 'RESOLVED'], ['all', 'ALL']].map(([k, l]) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              className={`px-4 py-2 font-mono text-xs tracking-wider ${filter === k ? 'bg-tiffany-500 text-black' : 'bg-zinc-900 text-zinc-400 hover:bg-zinc-800'}`}
              data-testid={`filter-${k}`}
            >
              {l}
            </button>
          ))}
        </div>

        {/* Crash list */}
        {loading ? (
          <p className="text-center py-12 font-mono text-zinc-500">LOADING_</p>
        ) : filteredCrashes.length === 0 ? (
          <div className="text-center py-16 border border-zinc-900">
            <CheckCircle className="mx-auto mb-3 text-tiffany-500/50" size={36} />
            <p className="font-mono text-zinc-500 tracking-widest text-sm">NO CRASHES · GOOD JOB</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredCrashes.map(c => {
              const sev = SEVERITY_COLORS[c.severity] || SEVERITY_COLORS[2];
              const SourceIcon = SOURCE_ICONS[c.source] || Server;
              const isOpen = expanded === c.id;
              return (
                <div key={c.id} className={`border ${sev.border} bg-zinc-950`} data-testid={`crash-${c.id}`}>
                  <button
                    onClick={() => setExpanded(isOpen ? null : c.id)}
                    className="w-full text-left p-4 hover:bg-zinc-900/40"
                  >
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1.5">
                          <SourceIcon className="w-3.5 h-3.5 text-zinc-500" />
                          <span className={`px-2 py-0.5 ${sev.bg} ${sev.text} font-mono text-[10px] tracking-widest`}>
                            {sev.label}
                          </span>
                          <span className="px-2 py-0.5 bg-purple-500/15 text-purple-300 font-mono text-[10px] tracking-widest">
                            {c.tag}
                          </span>
                          <span className="font-mono text-[10px] text-zinc-500">
                            {c.occurrences}× · LAST {new Date(c.last_seen).toLocaleString()}
                          </span>
                          {c.resolved && (
                            <span className="px-2 py-0.5 bg-tiffany-500/15 text-tiffany-400 font-mono text-[10px] tracking-widest">
                              RESOLVED
                            </span>
                          )}
                        </div>
                        <p className="font-mono text-sm text-white truncate">
                          {c.error_class}: {c.error_message?.slice(0, 160)}
                        </p>
                        {c.url && (
                          <p className="font-mono text-[10px] text-zinc-600 truncate mt-0.5">{c.url}</p>
                        )}
                      </div>
                    </div>
                  </button>

                  {isOpen && (
                    <div className="border-t border-zinc-800 p-4 space-y-4" onClick={(e) => e.stopPropagation()}>
                      {c.stack && (
                        <div>
                          <p className="font-mono text-[10px] text-zinc-500 tracking-widest mb-1.5">STACK TRACE</p>
                          <pre className="bg-black border border-zinc-800 p-3 font-mono text-[10px] text-zinc-400 overflow-x-auto max-h-72 whitespace-pre">{c.stack}</pre>
                        </div>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[11px] font-mono">
                        <Field label="FIRST SEEN" value={new Date(c.first_seen).toLocaleString()} />
                        <Field label="LAST SEEN" value={new Date(c.last_seen).toLocaleString()} />
                        <Field label="USER" value={c.last_user_email || c.user_email || '—'} />
                        <Field label="IP" value={c.last_ip || c.ip || '—'} />
                        <Field label="USER AGENT" value={c.last_user_agent || c.user_agent || '—'} />
                        <Field label="HTTP STATUS" value={c.last_http_status || c.http_status || '—'} />
                      </div>

                      <div>
                        <p className="font-mono text-[10px] text-zinc-500 tracking-widest mb-1.5">ADMIN NOTES · ROOT CAUSE</p>
                        <textarea
                          defaultValue={c.admin_notes || ''}
                          onBlur={(e) => { if (e.target.value !== (c.admin_notes || '')) saveNotes(c.id, e.target.value); }}
                          placeholder="Write the actual root cause + fix here so it doesn't recur..."
                          className="w-full bg-black border border-zinc-800 p-3 font-mono text-xs text-white placeholder-zinc-600 min-h-[80px]"
                          data-testid={`notes-${c.id}`}
                        />
                      </div>

                      <div className="flex flex-wrap gap-2">
                        {c.resolved ? (
                          <button
                            onClick={() => markResolved(c.id, false)}
                            className="px-4 py-2 border border-yellow-500/40 text-yellow-400 font-mono text-xs tracking-wider hover:bg-yellow-500/10"
                            data-testid={`reopen-${c.id}`}
                          >
                            REOPEN
                          </button>
                        ) : (
                          <button
                            onClick={() => markResolved(c.id, true)}
                            className="px-4 py-2 bg-tiffany-500 text-black font-mono text-xs tracking-wider hover:bg-tiffany-400"
                            data-testid={`resolve-${c.id}`}
                          >
                            <CheckCircle className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
                            MARK RESOLVED
                          </button>
                        )}
                        <button
                          onClick={() => deleteCrash(c.id)}
                          className="px-4 py-2 border border-red-500/40 text-red-400 font-mono text-xs tracking-wider hover:bg-red-500/10 ml-auto"
                          data-testid={`delete-${c.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
                          DELETE
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
